import type { Locale } from "./i18n";
import { bcp47 } from "./i18n";

function zonedDateParts(timeZone?: string): { year: number; month: number; day: number } {
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  const parts = dtf.formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day"))
  };
}

export function formatTime(rawHHmm: string, mode: "24h" | "12h"): string {
  const [hh, mm] = rawHHmm.split(":").map((n) => Number(n));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return rawHHmm;

  if (mode === "24h") return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;

  const suffix = hh >= 12 ? "PM" : "AM";
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${String(mm).padStart(2, "0")} ${suffix}`;
}

export function todayISO(timeZone?: string): string {
  const p = zonedDateParts(timeZone);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

export function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

export function normalizeISODate(value: string, fallback?: string): string {
  if (!isIsoDate(value)) return fallback ?? value;
  const [y, m, d] = value.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (
    Number.isNaN(dt.getTime()) ||
    dt.getUTCFullYear() !== y ||
    dt.getUTCMonth() !== m - 1 ||
    dt.getUTCDate() !== d
  ) {
    return fallback ?? value;
  }
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function monthYearFromISO(iso: string): { month: number; year: number } {
  const [y, m] = normalizeISODate(iso, iso).split("-").map(Number);
  return { year: y, month: m };
}

export function monthInputValue(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}

export function parseMonthInput(value: string): { year: number; month: number } | null {
  if (!/^\d{4}-\d{2}$/.test(value)) return null;
  const [year, month] = value.split("-").map(Number);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) return null;
  return { year, month };
}

export function firstDayOfMonthISO(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}-01`;
}

export function addDaysISO(iso: string, days: number): string {
  const normalized = normalizeISODate(iso, iso);
  const [y, m, d] = normalized.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yyyy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function shiftMonthISO(iso: string, deltaMonths: number): string {
  const normalized = normalizeISODate(iso, iso);
  const [y, m, d] = normalized.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + deltaMonths, d));
  const yyyy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function localeTag(locale?: Locale): string | undefined {
  return locale ? bcp47(locale) : undefined;
}

export function weekdayShort(iso: string, timeZone?: string, locale?: Locale): string {
  const normalized = normalizeISODate(iso, iso);
  const [y, m, d] = normalized.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(localeTag(locale), { weekday: "short", timeZone });
}

export function monthLabel(year: number, month: number, timeZone?: string, locale?: Locale): string {
  const dt = new Date(Date.UTC(year, month - 1, 1));
  return dt.toLocaleDateString(localeTag(locale), { month: "long", year: "numeric", timeZone });
}

export function formatDateLong(iso: string, timeZone?: string, locale?: Locale): string {
  const normalized = normalizeISODate(iso, iso);
  const [y, m, d] = normalized.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(localeTag(locale), {
    timeZone,
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}

export function formatDateTimeInZone(iso: string, timeZone?: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleString(undefined, {
    timeZone,
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}
